import {
  FlagCircleTwoTone,
  KeyboardArrowRightTwoTone,
  LocalActivityTwoTone,
  LoginTwoTone,
  LogoutTwoTone,
  PersonTwoTone,
  ShoppingBagTwoTone,
  SwitchAccountTwoTone,
  WorkTwoTone,
} from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { individualBalances } from '../api';
import { useAuth } from '../hooks/useAuth';
import { IMemberSalesOrderInfoResult } from '../utils/types';

interface NavMenuListProps {
  onLogout: () => void;
  onCloseNav?: () => void;
  firstName?: string;
  lastName?: string;
  userPermissions?: string[];
  employee?: boolean;
  showArrows?: boolean;
}

declare let OTS_URL: string;

export function NavMenuList({
  onCloseNav,
  firstName,
  lastName,
  userPermissions,
  employee,
  showArrows,
}: NavMenuListProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [showNavGuide, setShowNavGuide] = useState(false);

  useEffect(() => {
    fetchBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBalances = async () => {
    try {
      const orders: IMemberSalesOrderInfoResult[] = await individualBalances(
        true,
        token,
      );
      const filteredOrders = orders.filter(
        (order) => order.positionTypeCode !== 'CUS',
      );
      if (filteredOrders.length >= 1) {
        setShowNavGuide(true);
      } else setShowNavGuide(false);
    } catch (e: any) {
      console.log(e.detail, 'error');
    }
  };

  const handleChangeAccount = () => {
    const finalUrl =
      `${OTS_URL}` + `/#/login-with-token?token=${token}&next=/hedjaz/menu`;
    window.location.href = finalUrl;
  };

  return (
    <Box>
      {firstName && lastName && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              marginTop: '24px',
              height: '74px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#FAFAFA',
                border: '2px solid #EEE',
                borderRadius: '50%',
                textAlign: 'center',
                width: '45px',
                minWidth: '45px',
                height: '45px',
              }}
            >
              <Typography sx={{ mx: 'auto' }}>
                {`${firstName[0].toUpperCase()}${lastName[0].toUpperCase()}`}
              </Typography>
            </Box>
            <Typography sx={{ ml: 1 }}>{`${firstName} ${lastName}`}</Typography>
          </Box>
        </Box>
      )}

      <Button
        component={Link}
        to="/account/orders"
        onClick={() => onCloseNav && onCloseNav()}
        variant="text"
        sx={{
          display: 'flex',
        }}
        startIcon={<ShoppingBagTwoTone />}
        endIcon={showArrows ? <KeyboardArrowRightTwoTone /> : undefined}
        color={
          location.pathname.startsWith('/account/orders')
            ? 'primary'
            : 'inherit'
        }
      >
        <Box sx={{ flex: 1 }}>{t('navMenuList.orders')}</Box>
      </Button>
      <Button
        component={Link}
        to="/account/infos"
        onClick={() => onCloseNav && onCloseNav()}
        variant="text"
        sx={{
          display: 'flex',
        }}
        startIcon={<PersonTwoTone />}
        endIcon={showArrows ? <KeyboardArrowRightTwoTone /> : undefined}
        color={
          location.pathname.startsWith('/account/infos') ? 'primary' : 'inherit'
        }
      >
        <Box sx={{ flex: 1 }}>{t('navMenuList.accountInfos')}</Box>
      </Button>
      {!!userPermissions?.includes('getMemberIndividualPointTransaction') && (
        <Button
          component={Link}
          to="/account/points"
          onClick={() => onCloseNav && onCloseNav()}
          variant="text"
          sx={{
            display: 'flex',
          }}
          startIcon={<LocalActivityTwoTone />}
          endIcon={showArrows ? <KeyboardArrowRightTwoTone /> : undefined}
          color={
            location.pathname.startsWith('/account/points')
              ? 'primary'
              : 'inherit'
          }
        >
          <Box sx={{ flex: 1 }}>{t('navMenuList.points')}</Box>
        </Button>
      )}
      {userPermissions?.includes('getGuideSalesIndividualTourCustomerCount') &&
        showNavGuide && (
          <Button
            component={Link}
            to="/account/guidance"
            onClick={() => onCloseNav && onCloseNav()}
            variant="text"
            sx={{
              display: 'flex',
            }}
            startIcon={<FlagCircleTwoTone />}
            endIcon={showArrows ? <KeyboardArrowRightTwoTone /> : undefined}
            color={
              location.pathname.startsWith('/account/guidance')
                ? 'primary'
                : 'inherit'
            }
          >
            <Box sx={{ flex: 1 }}>{t('navMenuList.guidance')}</Box>
          </Button>
        )}
      {employee && (
        <Button
          variant="text"
          color="inherit"
          onClick={handleChangeAccount}
          startIcon={<WorkTwoTone />}
        >
          {t('navMenuList.switchToOtsApp')}
        </Button>
      )}
      <Button
        component={Link}
        to="/account/logout"
        onClick={() => onCloseNav && onCloseNav()}
        variant="text"
        sx={{
          display: 'flex',
          justifyContent: 'start',
        }}
        startIcon={<LogoutTwoTone />}
        color="inherit"
      >
        {t('navMenuList.logout')}
      </Button>
    </Box>
  );
}

export function NavMenuListSkeleton() {
  return (
    <Box sx={{ pt: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Skeleton variant="circular" width={49} height={49} />
        <Skeleton variant="text" height={30} width={140} />
      </Stack>
      <Stack spacing={0.5}>
        {Array(5)
          .fill(0)
          .map((_, index) => (
            <Skeleton
              key={index}
              variant="text"
              width={60 + Math.random() * 100}
              height={30}
            />
          ))}
      </Stack>
    </Box>
  );
}
